import React, { useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import { toast } from "react-toastify";
import { MonthlyDailyReport } from "../../Services/Dashboard";
import moment from "moment/moment";
import SpinnerModal from "../../Spinner/SpinnerModal";
import DataTableExtensions from "react-data-table-component-extensions";
import { Button } from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import "./Reports.css";

const DailyReports = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [dailyReports, setDailyReports] = useState([]);
  const [FilterYear, setYear] = useState(new Date().getFullYear().toString());

  // Fetch Monthly Daily Reports
  const GetMonthlyDaily = async (Year) => {
    try {
      setIsLoading(true);
      const LabID = sessionStorage.getItem("LabID");
      const response = await MonthlyDailyReport(LabID, Year);
      setDailyReports(response.dailyReports || []);
    } catch (error) {
      toast.error("Unable to get data", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const currentDate = new Date();
    const Year = currentDate.getFullYear();
    setYear(Year.toString());
    GetMonthlyDaily(Year);
  }, []);

  // Filter handlers
  const handleChange = (e) => {
    const selectedYear = e.target.value;
    setYear(selectedYear);
  };

  const Applyfilter = () => {
    GetMonthlyDaily(FilterYear);
  };

  // Modern styled columns using common class utilities
  const columns = [
    {
      name: "Date",
      selector: (row) => moment(row.recordDate).format("D MMM YYYY"),
      cellExport: (row) => row.recordDate,
      sortable: true,
      grow: 1.2,
      cell: (row) => (
        <span className="commont-Font-Bold text-dark">
          {moment(row.recordDate).format("D MMM YYYY")}
        </span>
      ),
    },
    {
      name: "Day",
      selector: (row) => row.dayName,
      cellExport: (row) => row.dayName,
      sortable: true,
      cell: (row) => (
        <span className="badge bg-light text-secondary border common-FontSize-small px-2 py-1">
          {row.dayName}
        </span>
      ),
    },
    {
      name: "Tests",
      selector: (row) => row.totalTest,
      cellExport: (row) => row.totalTest,
      sortable: true,
      right: true,
      cell: (row) => (
        <span className="badge bg-primary-subtle common-color commont-Font-Bold px-2 py-1">
          {row.totalTest || 0}
        </span>
      ),
    },
    {
      name: "Sale",
      selector: (row) => row.totalGrandTotal,
      cellExport: (row) => row.totalGrandTotal,
      sortable: true,
      right: true,
      cell: (row) => (
        <span className="common-FontWeight text-dark">
          {Number(row.totalGrandTotal || 0).toLocaleString()}
        </span>
      ),
    },
    {
      name: "Discount",
      selector: (row) => row.totalDiscount,
      cellExport: (row) => row.totalDiscount,
      sortable: true,
      right: true,
      cell: (row) => (
        <span className="text-muted">
          {Number(row.totalDiscount || 0).toLocaleString()}
        </span>
      ),
    },
    {
      name: "Received Amount",
      selector: (row) => row.receivedAmount,
      cellExport: (row) => row.receivedAmount,
      sortable: true,
      right: true,
      cell: (row) => (
        <span className="commont-Font-Bold text-success">
          {Number(row.receivedAmount || 0).toLocaleString()}
        </span>
      ),
    },
    {
      name: "Due Amount",
      selector: (row) => row.dueAmount,
      cellExport: (row) => row.dueAmount,
      sortable: true,
      right: true,
      cell: (row) => (
        <span
          className={`commont-Font-Bold ${
            Number(row.dueAmount) > 0 ? "text-danger" : "text-secondary"
          }`}
        >
          {Number(row.dueAmount || 0).toLocaleString()}
        </span>
      ),
    },
  ];

  const tableData = {
    columns,
    data: dailyReports,
  };

  const customStyles = {
    header: {
      style: {
        minHeight: "56px",
      },
    },
    headRow: {
      style: {
        backgroundColor: "#f8fafc",
        borderTop: "1px solid #e2e8f0",
        borderBottom: "2px solid #cbd5e1",
      },
    },
    headCells: {
      style: {
        fontSize: "12px",
        fontWeight: "600",
        color: "#475569",
        paddingLeft: "16px",
        paddingRight: "16px",
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
      },
    },
    rows: {
      style: {
        fontSize: "12px",
        color: "#1e293b",
        minHeight: "48px",
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
        "&:not(:last-child)": {
          borderBottomStyle: "solid",
          borderBottomWidth: "1px",
          borderBottomColor: "#f1f5f9",
        },
        "&:hover": {
          backgroundColor: "#f8fafc !important",
          transition: "all 0.2s ease",
        },
      },
    },
    pagination: {
      style: {
        borderTop: "1px solid #e2e8f0",
        fontSize: "12px",
      },
    },
  };

  return (
    <>
      {isLoading && <SpinnerModal isLoading={isLoading} />}
      <div className="reports-container common-FontFamily">
        {/* Filter Toolbar Box */}
        <div className="p-3 mb-3 common-bg-white common-box-border common-br-3px common-box-shadow">
          <div className="row align-items-center justify-content-between g-3">
            <div className="col-auto d-flex align-items-center gap-2">
              <CalendarTodayIcon className="common-color fs-5" />
              <h6 className="mb-0 commont-Font-Bold text-dark common-FontSize-small">
                Daily Sales & Activity
              </h6>
            </div>

            <div className="col-auto d-flex align-items-center gap-2">
              <div className="d-flex align-items-center gap-2">
                <label htmlFor="Year" className="common-Labels mb-0">
                  Year Filter<span className="common-Asterisk">*</span>
                </label>
                <select
                  className="form-select common-Input"
                  id="Year"
                  name="Year"
                  value={FilterYear}
                  required
                  onChange={handleChange}
                  style={{ width: "110px" }}
                >
                  <option value="2026">2026</option>
                  <option value="2025">2025</option>
                  <option value="2024">2024</option>
                  <option value="2023">2023</option>
                </select>
              </div>

              <Button
                variant="contained"
                onClick={Applyfilter}
                type="button"
                className="common-button text-capitalize"
                startIcon={<FilterListIcon />}
                style={{ backgroundColor: "#2c6bd7", minWidth: "80px" }}
              >
                Apply
              </Button>
            </div>
          </div>
        </div>

        {/* Data Table Card Wrapper */}
        <div className="p-3 common-bg-white common-box-border common-br-3px common-box-shadow">
          <DataTableExtensions {...tableData}>
            <DataTable
              pagination
              fixedHeader
              fixedHeaderScrollHeight="70vh"
              highlightOnHover
              pointerOnHover
              responsive
              striped
              customStyles={customStyles}
              noDataComponent={
                <div className="p-4 common-text-center text-muted common-FontSize-small">
                  No records found for the selected year.
                </div>
              }
            />
          </DataTableExtensions>
        </div>
      </div>
    </>
  );
};

export default DailyReports;